import { isEditableKeyTarget } from 'features/knowledgeDocumentViewer/components/viewer/shared/viewerHotkeys'
import type { ViewLayout } from 'features/knowledgeDocumentViewer/types'
import { useEffect } from 'react'

interface UsePageNavigationProps {
  currentPageNumber: number
  totalPages: number
  onPageChange: (pageNumber: number) => void
  pageStep?: number
  enabled?: boolean
  viewLayout?: ViewLayout
}

export function usePageNavigation({
  currentPageNumber,
  totalPages,
  onPageChange,
  pageStep = 1,
  enabled = true,
  viewLayout = 'single',
}: UsePageNavigationProps) {
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableKeyTarget(event.target)) return

      const isContinuous = viewLayout === 'continuous'

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          if (currentPageNumber > 1) {
            onPageChange(Math.max(1, currentPageNumber - pageStep))
          }
          break
        case 'ArrowRight':
          event.preventDefault()
          if (currentPageNumber < totalPages) {
            onPageChange(Math.min(totalPages, currentPageNumber + pageStep))
          }
          break
        case 'ArrowUp':
          if (!isContinuous) {
            event.preventDefault()
            if (currentPageNumber > 1) {
              onPageChange(Math.max(1, currentPageNumber - pageStep))
            }
          }
          break
        case 'ArrowDown':
          if (!isContinuous) {
            event.preventDefault()
            if (currentPageNumber < totalPages) {
              onPageChange(Math.min(totalPages, currentPageNumber + pageStep))
            }
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    currentPageNumber,
    totalPages,
    onPageChange,
    pageStep,
    enabled,
    viewLayout,
  ])
}
